// TeacherPortal.js - VERSION 3.3.0 (VISIBILITY CONTROLS)
import React, { useState, useEffect, useRef } from 'react';
import {
  Upload, FileText, BarChart3, Settings,
  User, LogOut, Eye, Trash2,
  AlertCircle, Search, ChevronDown, BookOpen, Calendar, Building, School,
  Download, Lock, Folder, HardDrive, ShieldCheck, MoreVertical, Plus, Move, Copy,
  Check, MoreHorizontal, MousePointer2, Clock, FolderPlus, ArrowLeft, ChevronRight,
  Share2, Clipboard, X, Maximize2, Info, LayoutGrid, Camera, Shield, Key, EyeOff, Globe, Menu,
  MessageSquare, Send, CheckCheck, Paperclip,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link2, Palette, Save, FileDown, Edit3, Strikethrough
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { teacherAPI, authAPI, publicAPI, personalStorageAPI, api, chatAPI } from '../services/api';

const TeacherPortal = ({ embeddedVault = false }) => {
  const [activeTab, setActiveTab] = useState(embeddedVault ? 'vault' : 'dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [teacher, setTeacher] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [stats, setStats] = useState([]);

  // Vault States
  const [vaultFiles, setVaultFiles] = useState([]);
  const [vaultFolders, setVaultFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultSearch, setVaultSearch] = useState('');
  const [vaultCategory, setVaultCategory] = useState('all');
  const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [targetCategoryForNewFolder, setTargetCategoryForNewFolder] = useState('all');
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedItemForMove, setSelectedItemForMove] = useState(null);

  // Chat States
  const [contacts, setContacts] = useState([]);
  const [chatSearch, setChatSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatAttachment, setChatAttachment] = useState(null);
  const chatFileInputRef = useRef(null);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const [showInfoItem, setShowInfoItem] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1024);
  const [mobileVaultStep, setMobileVaultStep] = useState('categories');
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const [isVaultNavOpen, setIsVaultNavOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareFileId, setShareFileId] = useState(null);

  // Note Editor States
  const [isNoteEditorOpen, setIsNoteEditorOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [isSaveDropdownOpen, setIsSaveDropdownOpen] = useState(false);
  const [saveFileName, setSaveFileName] = useState('');
  const [saveFolderId, setSaveFolderId] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newSaveFolderName, setNewSaveFolderName] = useState('');
  const [saveFormat, setSaveFormat] = useState('html');
  const [insertLinkModalOpen, setInsertLinkModalOpen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const editorRef = useRef(null);

  // Profile Management States
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    department: '',
    school: '',
    profileImage: null,
    showAvatarPublicly: true
  });

  // Upload States
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', course: '', file: null });
  const [departments, setDepartments] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [clipboard, setClipboard] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const API_BASE_URL = api.defaults.baseURL;

  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const baseUrlObject = new URL(API_BASE_URL);
    const host = `${baseUrlObject.protocol}//${baseUrlObject.host}`;
    if (url.startsWith('/api')) return `${host}${url}`;
    return `${API_BASE_URL}${url}`;
  };

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (!embeddedVault && tab) setActiveTab(tab);
  }, [location.search]);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/'); return; }
      try {
        const response = embeddedVault ? await authAPI.getCurrentUser() : await teacherAPI.getProfile();
        const userData = response.data;
        if (embeddedVault && (userData.role !== 'school_admin' || userData.permissions?.canUsePersonalFile === false)) {
          navigate('/');
          return;
        }
        setTeacher(userData);
        setProfileData({
          fullName: userData.fullName,
          email: userData.email,
          department: embeddedVault ? 'School Administrator' : userData.teacherProfile?.department?.name || 'N/A',
          school: embeddedVault ? userData.school?.name || 'School Administration' : userData.teacherProfile?.school?.name || 'N/A',
          profileImage: getFullImageUrl(userData.profileImage?.url),
          showAvatarPublicly: userData.showAvatarPublicly ?? true
        });
        if (!embeddedVault) loadDashboardData();
        loadVaultFiles();
        const schoolId = embeddedVault ? null : userData.teacherProfile?.school?._id || userData.teacherProfile?.school;
        if (schoolId) {
          const deptRes = await publicAPI.getSchoolDepartments(schoolId);
          let allDepts = deptRes.data || [];

          // Apply administrative strict whitelisting access control
          const permittedRaw = userData.teacherProfile?.permittedDepartments || [];
          // permittedDepartments may be populated objects {_id, name} or plain id strings
          const permittedIds = permittedRaw.map(d => (d._id ? d._id.toString() : d.toString()));
          allDepts = allDepts.filter(d => permittedIds.includes((d._id || d.id).toString()));

          setDepartments(allDepts);
          const myDeptId = userData.teacherProfile?.department?._id || userData.teacherProfile?.department;

          // If current primary dept is not in permitted list, select the first permitted one
          if (myDeptId && allDepts.some(d => (d._id || d.id) === myDeptId)) {
            setSelectedDept(myDeptId);
          } else if (allDepts.length > 0) {
            setSelectedDept(allDepts[0]._id || allDepts[0].id);
          }
        }
      } catch (err) { navigate('/'); }
    };
    init();
  }, [navigate, embeddedVault]);

  useEffect(() => {
    if (selectedDept) {
      setSelectedYear('');
      setUploadForm(prev => ({ ...prev, course: '' }));
      setCourses([]);
      const deptObj = departments.find(d => (d._id || d.id) === selectedDept);
      if (deptObj && deptObj.activeYears && deptObj.activeYears.length > 0) {
        setAvailableYears(deptObj.activeYears.sort((a, b) => a - b));
      } else {
        setAvailableYears([1, 2, 3, 4, 5]);
      }
    }
  }, [selectedDept, departments]);

  useEffect(() => {
    if (selectedDept && selectedYear) {
      const fetchCourses = async () => {
        try {
          const res = await publicAPI.getCourses({ department: selectedDept, year: selectedYear });
          setCourses(res.data || []);
        } catch (err) { setCourses([]); }
      };
      fetchCourses();
    } else { setCourses([]); }
  }, [selectedYear, selectedDept]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-trigger') && !e.target.closest('.bottom-sheet')) setActiveMenuId(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Chat Handlers & Effects
  const loadContacts = async (silent = false) => {
    try {
      if (!silent) setLoadingChat(true);
      const res = await chatAPI.getContacts();
      if (res.success) {
        setContacts(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat contacts', err);
    } finally {
      if (!silent) setLoadingChat(false);
    }
  };

  const loadMessages = async (contactId, silent = false) => {
    try {
      if (!silent) setLoadingMessages(true);
      const res = await chatAPI.getMessages(contactId);
      if (res.success) {
        setMessages(res.data || []);
      }
    } catch (err) {
      console.error('Failed to load chat messages', err);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  const handleSelectContact = async (contact) => {
    setSelectedContact(contact);
    setNewMessage('');
    await loadMessages(contact._id);
    await chatAPI.markAsRead(contact._id);
    loadContacts(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !chatAttachment) return;
    if (!selectedContact) return;
    try {
      setSendingMsg(true);
      let res;
      if (chatAttachment) {
        const formData = new FormData();
        formData.append('receiverId', selectedContact._id);
        if (newMessage.trim()) formData.append('content', newMessage.trim());
        formData.append('attachment', chatAttachment);
        res = await chatAPI.sendMessage(formData);
      } else {
        res = await chatAPI.sendMessage({
          receiverId: selectedContact._id,
          content: newMessage.trim()
        });
      }
      if (res.success) {
        setMessages(prev => [...prev, res.data]);
        setNewMessage('');
        setChatAttachment(null);
        loadContacts(true);
      }
    } catch (err) {
      console.error('Failed to send message', err);
    } finally {
      setSendingMsg(false);
    }
  };



  useEffect(() => {
    if (activeTab === 'chat') {
      loadContacts();
    }
  }, [activeTab]);

  useEffect(() => {
    // Initial global load for badges
    if (embeddedVault) return;
    loadContacts(true);
    
    // Background polling for new messages and badges
    const contactsInterval = setInterval(() => {
      loadContacts(true);
    }, 6000);
    return () => clearInterval(contactsInterval);
  }, [embeddedVault]);

  useEffect(() => {
    let msgInterval;
    if (activeTab === 'chat' && selectedContact) {
      msgInterval = setInterval(() => {
        loadMessages(selectedContact._id, true);
      }, 3000);
    }
    return () => {
      if (msgInterval) clearInterval(msgInterval);
    };
  }, [activeTab, selectedContact]);

  const totalUnreadChat = contacts.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  // Handlers
  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (e) { }
    localStorage.clear();
    navigate('/');
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, materialsData] = await Promise.all([
        teacherAPI.getDashboardStats(),
        teacherAPI.getMaterials({ limit: 5 })
      ]);
      setStats([
        { label: "Total Uploads", value: statsData.data.stats.totalUploads || 0 },
        { label: "Total Downloads", value: statsData.data.stats.totalDownloads || 0 },
        { label: "Active Courses", value: statsData.data.stats.courses || 0 },
      ]);
      setMaterials(materialsData.data.materials || []);
    } catch (err) { setError('Failed to load dashboard'); } finally { setLoading(false); }
  };

  const handlePreview = (material) => {
    if (typeof material === 'object' && window.openFilePreview) {
      window.openFilePreview(material);
    } else {
      const id = typeof material === 'object' ? material._id : material;
      if (window.openFilePreview) {
        window.openFilePreview({ _id: id, title: 'Material Preview' });
      } else {
        const previewUrl = publicAPI.getPreviewUrl(id);
        window.open(previewUrl, '_blank');
      }
    }
  };

  const handleDownload = (materialId) => {
    const downloadUrl = publicAPI.getDownloadUrl(materialId);
    window.open(downloadUrl, '_blank');
  };

  const handleMaterialDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await teacherAPI.deleteMaterial(id);
      setSuccess("Deleted successfully");
      setMaterials(prev => prev.filter(m => m._id !== id));
    } catch (err) { setError("Failed to delete"); }
  };

  const handleFileChange = (e) => {
    setUploadForm({ ...uploadForm, file: e.target.files[0] });
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.course) { setError("Please select a course."); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const formData = new FormData();
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('course', uploadForm.course);
      formData.append('file', uploadForm.file);
      await teacherAPI.uploadMaterial(formData);
      setSuccess('Material uploaded successfully!');
      setUploadForm({ title: '', description: '', course: '', file: null });
      setSelectedDept(''); setSelectedYear('');
      loadDashboardData();
      setTimeout(() => setActiveTab('dashboard'), 1500);
    } catch (err) { 
      setError(err.message || (typeof err === 'string' ? err : 'Upload failed')); 
    } finally { 
      setLoading(false); 
    }
  };

  // Profile Logic
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const res = await authAPI.updateAvatar(formData);
      setProfileData(prev => ({ ...prev, profileImage: getFullImageUrl(res.data.profileImage?.url) }));
      setSuccess('Avatar updated!');
    } catch (err) { setError('Failed to update avatar'); } finally { setLoading(false); }
  };

  const toggleVisibility = async () => {
    const newVal = !profileData.showAvatarPublicly;
    setLoading(true);
    try {
      await authAPI.updateSettings({ showAvatarPublicly: newVal });
      setProfileData(prev => ({ ...prev, showAvatarPublicly: newVal }));
      setSuccess(`Avatar visibility: ${newVal ? 'Public' : 'Private'}`);
    } catch (err) { setError('Failed to update visibility'); } finally { setLoading(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New passwords do not match"); return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      setSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
    } catch (err) { setError(err.message || 'Failed to change password'); } finally { setLoading(false); }
  };

  // Vault Handlers
  const loadFolders = async (parentId = null, category = null) => {
    try {
      const res = await personalStorageAPI.getFolders({ parentFolder: parentId, category: category || vaultCategory });
      setVaultFolders(res.data || []);
    } catch (err) { console.error(err); }
  };

  const loadVaultFiles = async (categoryOverride = null, folderOverride = undefined) => {
    try {
      setVaultLoading(true);
      const cat = categoryOverride !== null ? categoryOverride : vaultCategory;
      const fld = folderOverride !== undefined ? folderOverride : currentFolder;
      const res = await personalStorageAPI.getMyFiles({ search: vaultSearch, category: cat, folder: fld });
      setVaultFiles(res.data || []);
      await loadFolders(fld, cat);
    } catch (err) { console.error(err); } finally { setVaultLoading(false); }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await personalStorageAPI.createFolder({ name: newFolderName, parentFolder: currentFolder, category: targetCategoryForNewFolder });
      setNewFolderName(''); setIsNewFolderModalOpen(false); loadFolders(currentFolder, vaultCategory);
      setSuccess("Folder Created");
    } catch (err) { setError('Failed'); }
  };

  const handleCopy = (item, type = 'file') => {
    setClipboard({ id: item._id, type, title: item.title || item.name });
    setSuccess('Copied to Clipboard'); setActiveMenuId(null);
  };

  const handlePaste = async () => {
    if (!clipboard) return;
    setLoading(true);
    try {
      if (clipboard.type === 'file') { await personalStorageAPI.copyFile(clipboard.id, { targetFolderId: currentFolder }); }
      setSuccess('Pasted Successfully'); loadVaultFiles();
    } catch (err) { setError('Paste failed'); } finally { setLoading(false); }
  };

  const handleShare = (id) => {
    setShareFileId(id);
    setShareModalOpen(true);
    setActiveMenuId(null);
  };

  const handleMoveItem = async (targetFolderId) => {
    if (!selectedItemForMove) return;
    try {
      setLoading(true);
      if (selectedItemForMove.type === 'file') { await personalStorageAPI.moveFile({ fileId: selectedItemForMove._id, targetFolderId }); }
      else { await personalStorageAPI.moveFolder({ folderId: selectedItemForMove._id, targetFolderId }); }
      setIsMoveModalOpen(false); setSelectedItemForMove(null); loadVaultFiles();
      setSuccess("Moved Successfully");
    } catch (err) { setError('Move failed'); } finally { setLoading(false); }
  };


  const handleVaultUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name);
      if (currentFolder) formData.append('folder', currentFolder);
      await personalStorageAPI.uploadFile(formData);
      setSuccess('Secured!'); loadVaultFiles();
    } catch (err) { 
      setError(err.message || (typeof err === 'string' ? err : 'Upload failed')); 
    } finally { 
      setLoading(false); 
      e.target.value = ''; 
    }
  };

  const navigateToFolder = (folder) => {
    if (folder === null) { setCurrentFolder(null); setFolderPath([]); loadVaultFiles(null, null); }
    else { setCurrentFolder(folder._id); setFolderPath([...folderPath, folder]); loadVaultFiles(null, folder._id); }
  };

  const handleGoBack = () => {
    if (previewItem) { setPreviewItem(null); if (isMobileView) setMobileVaultStep('files'); return; }
    if (folderPath.length === 0) { if (isMobileView) setMobileVaultStep('categories'); return; }
    const newPath = [...folderPath]; newPath.pop();
    const prevFolder = newPath.length > 0 ? newPath[newPath.length - 1] : null;
    setFolderPath(newPath); setCurrentFolder(prevFolder ? prevFolder._id : null);
    loadVaultFiles(null, prevFolder ? prevFolder._id : null);
  };

  const handleCategorySelect = (catId) => {
    setVaultCategory(catId); loadVaultFiles(catId);
    if (isMobileView) setMobileVaultStep('files');
  };

  const openPreview = (file) => {
    setPreviewItem(file);
    if (isMobileView) setMobileVaultStep('preview');
  };

  const openNewNoteEditor = () => {
    setEditingNoteId(null);
    setNoteTitle('');
    setIsNoteEditorOpen(true);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = '<div>Start typing your notes here...</div>';
        editorRef.current.focus();
        try {
          document.execCommand('styleWithCSS', false, true);
        } catch (e) {
          console.warn('styleWithCSS error:', e);
        }
      }
    }, 100);
  };

  const openNoteForEditing = async (file) => {
    setLoading(true);
    try {
      const response = await fetch(personalStorageAPI.downloadFile(file._id));
      if (!response.ok) throw new Error('Download failed');
      const text = await response.text();
      
      setEditingNoteId(file._id);
      setNoteTitle(file.title.replace(/\.(html|docx|ppt|pdf|txt)$/i, ''));
      setIsNoteEditorOpen(true);
      
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = text;
          editorRef.current.focus();
          try {
            document.execCommand('styleWithCSS', false, true);
          } catch (e) {
            console.warn('styleWithCSS error:', e);
          }
        }
      }, 100);
      
      setPreviewItem(null);
    } catch (err) {
      setError('Failed to load note content for editing');
    } finally {
      setLoading(false);
    }
  };

  const closeNoteEditor = () => {
    if (window.confirm('Discard unsaved changes?')) {
      setIsNoteEditorOpen(false);
      setEditingNoteId(null);
      setNoteTitle('');
    }
  };

  const execEditorCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const toggleSaveDropdown = () => {
    if (!isSaveDropdownOpen) {
      setSaveFileName(noteTitle.trim() || 'Untitled Note');
      setSaveFolderId(currentFolder || '');
      setIsCreatingFolder(false);
      setNewSaveFolderName('');
    }
    setIsSaveDropdownOpen(!isSaveDropdownOpen);
  };

  const handleSaveNote = async (format) => {
    setIsSaveDropdownOpen(false);
    
    // Determine the actual target folder
    let targetFolderId = saveFolderId;
    if (isCreatingFolder && newSaveFolderName.trim()) {
      setLoading(true);
      try {
        const res = await personalStorageAPI.createFolder({ 
          name: newSaveFolderName.trim(), 
          parentFolder: currentFolder, 
          category: vaultCategory 
        });
        targetFolderId = res.data._id;
        loadFolders(currentFolder, vaultCategory);
      } catch (err) {
        setError('Failed to create new folder for save.');
        setLoading(false);
        return;
      }
    }

    const finalTitle = noteTitle.trim() || 'Untitled Note';
    const finalFileName = saveFileName.trim() || finalTitle;
    const cleanFileName = finalFileName.replace(/[\s\W]+/g, '_');
    const htmlContent = editorRef.current ? editorRef.current.innerHTML : '';
    
    let blob;
    let mimeType;
    let fileNameWithExt;

    if (format === 'html') {
      mimeType = 'text/html';
      fileNameWithExt = `${cleanFileName}.html`;
      
      const docWrapper = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${finalTitle}</title>
  <style>
    body { font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #1e293b; line-height: 1.6; }
    h1 { color: #1e3a8a; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; }
    h2 { color: #2563eb; margin-top: 30px; }
    a { color: #2563eb; text-decoration: underline; }
    ul { list-style-type: disc; margin-left: 20px; padding-left: 10px; }
    ol { list-style-type: decimal; margin-left: 20px; padding-left: 10px; }
    li { margin-bottom: 5px; }
  </style>
</head>
<body>
  <h1>${finalTitle}</h1>
  <div>${htmlContent}</div>
</body>
</html>`;
      blob = new Blob([docWrapper], { type: mimeType });

    } else if (format === 'docx') {
      mimeType = 'application/msword';
      fileNameWithExt = `${cleanFileName}.docx`;
      
      const docWrapper = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <title>${finalTitle}</title>
  <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>90</w:Zoom></w:WordDocument></xml><![endif]-->
  <style>
    body { font-family: Arial, sans-serif; }
    h1 { color: #1e3a8a; font-size: 24pt; font-weight: bold; border-bottom: 1px solid #cccccc; }
    h2 { color: #2563eb; font-size: 18pt; font-weight: bold; }
    p { font-size: 11pt; line-height: 1.5; }
    ul { list-style-type: disc; margin-left: 20px; padding-left: 10px; }
    ol { list-style-type: decimal; margin-left: 20px; padding-left: 10px; }
    li { margin-bottom: 5px; }
  </style>
</head>
<body>
  <h1>${finalTitle}</h1>
  <div>${htmlContent}</div>
</body>
</html>`;
      blob = new Blob(['\ufeff' + docWrapper], { type: mimeType });

    } else if (format === 'ppt') {
      mimeType = 'application/vnd.ms-powerpoint';
      fileNameWithExt = `${cleanFileName}.ppt`;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      let slides = [];
      let currentSlideContent = '';
      
      Array.from(tempDiv.childNodes).forEach(node => {
        if (node.nodeName === 'H1' || node.nodeName === 'H2') {
          if (currentSlideContent) {
            slides.push(currentSlideContent);
          }
          currentSlideContent = `<div style="page-break-after: always; padding: 50px; background: white; border: 1px solid #ddd; height: 600px; box-sizing: border-box;">
            <h2 style="color: #2563eb; font-family: Arial; font-size: 32pt; margin-bottom: 20px;">${node.innerText}</h2>`;
        } else {
          if (!currentSlideContent) {
            currentSlideContent = `<div style="page-break-after: always; padding: 50px; background: white; border: 1px solid #ddd; height: 600px; box-sizing: border-box;">
              <h2 style="color: #2563eb; font-family: Arial; font-size: 32pt; margin-bottom: 20px;">${finalTitle}</h2>`;
          }
          currentSlideContent += `<div style="font-family: Arial; font-size: 18pt; line-height: 1.4; color: #444;">${node.outerHTML || node.textContent}</div>`;
        }
      });
      
      if (currentSlideContent) {
        currentSlideContent += '</div>';
        slides.push(currentSlideContent);
      }
      
      if (slides.length === 0) {
        slides.push(`<div style="page-break-after: always; padding: 50px; background: white; border: 1px solid #ddd; height: 600px; box-sizing: border-box;">
          <h2 style="color: #2563eb; font-family: Arial; font-size: 32pt; margin-bottom: 20px;">${finalTitle}</h2>
          <div style="font-family: Arial; font-size: 18pt; line-height: 1.4; color: #444;">${htmlContent}</div>
        </div>`);
      }

      const pptWrapper = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:p='urn:schemas-microsoft-com:office:powerpoint' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <title>${finalTitle}</title>
  <style>
    @page { size: landscape; }
    body { margin: 0; background-color: #f1f5f9; }
  </style>
</head>
<body>
  ${slides.join('\n')}
</body>
</html>`;

      blob = new Blob(['\ufeff' + pptWrapper], { type: mimeType });

    } else if (format === 'pdf') {
      mimeType = 'application/pdf';
      fileNameWithExt = `${cleanFileName}.pdf`;
      setLoading(true);

      const docWrapper = document.createElement('div');
      docWrapper.innerHTML = `
        <div style="font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; line-height: 1.6;">
          <h1 style="color: #1e3a8a; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; font-size: 28px;">${finalTitle}</h1>
          <div>${htmlContent}</div>
        </div>
      `;

      try {
        const opt = {
          margin:       0.5,
          filename:     fileNameWithExt,
          image:        { type: 'jpeg', quality: 0.98 },
          html2canvas:  { scale: 2, useCORS: true },
          jsPDF:        { unit: 'in', format: 'letter', orientation: 'portrait' }
        };
        blob = await html2pdf().set(opt).from(docWrapper).output('blob');
      } catch (err) {
        setError('Failed to generate PDF document');
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const filePayload = new File([blob], fileNameWithExt, { type: mimeType });
      const formData = new FormData();
      formData.append('file', filePayload);
      formData.append('title', finalFileName);
      if (targetFolderId) formData.append('folder', targetFolderId);

      if (editingNoteId) {
        await personalStorageAPI.updateFile(editingNoteId, formData);
        setSuccess('Note updated successfully!');
      } else {
        await personalStorageAPI.uploadFile(formData);
        setSuccess('Note compiled & saved to vault!');
      }
      
      loadVaultFiles();
      setIsNoteEditorOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save note');
    } finally {
      setLoading(false);
    }
  };

  const handleInsertFileLink = (file) => {
    setInsertLinkModalOpen(false);
    const downloadUrl = personalStorageAPI.downloadFile(file._id);
    const linkHTML = `<a href="${downloadUrl}" target="_blank" style="color: #2563eb; text-decoration: underline; font-weight: bold; display: inline-flex; align-items: center; gap: 4px;">📄 ${file.title}</a>&nbsp;`;
    
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, linkHTML);
    }
  };

  if (!teacher) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {!embeddedVault && <Header />}
      <div className="container mx-auto px-4 py-8">

        {error && <div className="mb-4 bg-red-50 text-red-800 p-3 rounded-lg flex items-center gap-2 animate-fade-in"><AlertCircle className="w-5 h-5" />{error}</div>}
        {success && <div className="mb-4 bg-green-50 text-green-800 p-3 rounded-lg flex items-center gap-2 animate-fade-in"><Check className="w-5 h-5" />{success}</div>}

        <div className="bg-white rounded-2xl shadow overflow-hidden mb-8 border border-gray-100">
          {!embeddedVault && <>
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 lg:p-8 text-white flex flex-col md:flex-row justify-between items-center md:items-center gap-6">
            <div className="flex flex-col sm:flex-row items-center text-center sm:text-left gap-4 sm:gap-6 w-full md:w-auto">
              <div className="relative group">
                <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/20 rounded-full border-4 border-white/20 overflow-hidden shadow-xl">
                  {profileData.profileImage ? (
                    <img src={profileData.profileImage} className="w-full h-full object-cover" alt="Profile" key={profileData.profileImage} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/50"><User className="w-8 h-8 lg:w-10 lg:h-10" /></div>
                  )}
                </div>
                {activeTab === 'profile' && (
                  <label className="absolute bottom-0 right-0 p-2 bg-blue-500 rounded-full cursor-pointer hover:bg-blue-400 transition-colors shadow-lg border-2 border-white">
                    <Camera className="w-3.5 h-3.5 text-white" />
                    <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
                  </label>
                )}
              </div>
              <div>
                <h1 className="text-xl lg:text-3xl font-black tracking-tight">{teacher.fullName}</h1>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1 text-blue-100 font-bold uppercase text-[10px] tracking-widest mt-2">
                  <span className="flex items-center gap-1">
                    <School className="w-3 h-3 text-blue-300 animate-pulse" />
                    <span>{profileData.school}</span>
                  </span>
                  <span className="opacity-30 hidden sm:inline">•</span>
                  <span className="flex items-center gap-1">
                    <Building className="w-3 h-3 text-blue-300" />
                    <span>{profileData.department}</span>
                  </span>
                  <span className="opacity-30 hidden sm:inline">•</span>
                  <span className="flex items-center gap-1.5 bg-white/10 px-2.5 py-0.5 rounded-full text-[9px] backdrop-blur-sm">
                    {profileData.showAvatarPublicly ? <Globe className="w-3 h-3 text-green-400" /> : <EyeOff className="w-3 h-3 text-amber-400" />}
                    <span>{profileData.showAvatarPublicly ? 'Public' : 'Private'}</span>
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="secondary" icon={LogOut} onClick={handleLogout} className="hidden md:flex font-bold uppercase tracking-widest text-[10px]">Logout</Button>
              <button
                onClick={() => setIsNavMenuOpen(true)}
                className="lg:hidden p-3 bg-white/20 rounded-xl hover:bg-white/30 transition-all text-white"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* NAV - Desktop Only */}
          <div className="border-b border-gray-200 hidden lg:block">
            <nav className="flex bg-slate-50/50">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                { id: 'materials', label: 'Library', icon: BookOpen },
                { id: 'upload', label: 'Publish', icon: Upload },
                { id: 'chat', label: 'Colleague Chat', icon: MessageSquare, badge: totalUnreadChat },
                { id: 'vault', label: 'Personal Files', icon: Lock },
                { id: 'profile', label: 'Profile', icon: User }
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setActiveTab(t.id); if (t.id === 'vault') setMobileVaultStep('files'); setPreviewItem(null); }}
                  className={`flex items-center gap-2 px-6 py-6 font-bold transition-all whitespace-nowrap text-[11px] uppercase tracking-[0.15em] relative ${activeTab === t.id ? 'text-blue-600 border-b-2 border-blue-600 bg-white' : 'text-gray-400 hover:bg-gray-100/50'}`}
                >
                  <t.icon className={`w-4 h-4 ${activeTab === t.id ? 'text-blue-600' : 'text-gray-300'}`} /> 
                  <span>{t.label}</span>
                  {t.badge > 0 && (
                    <span className="absolute top-2 right-1.5 bg-red-500 text-white text-[8px] font-black w-4.5 h-4.5 rounded-full flex items-center justify-center border-2 border-white animate-bounce px-1">
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* NAV - Mobile Display (Shows current tab title) */}
          <div className="lg:hidden border-b border-gray-100 px-6 py-4 flex items-center justify-between bg-slate-50/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                {(() => {
                  const Icon = [
                    { id: 'dashboard', icon: BarChart3 },
                    { id: 'materials', icon: BookOpen },
                    { id: 'upload', icon: Upload },
                    { id: 'chat', icon: MessageSquare },
                    { id: 'vault', icon: Lock },
                    { id: 'profile', icon: User }
                  ].find(t => t.id === activeTab)?.icon || BarChart3;
                  return <Icon className="w-5 h-5" />;
                })()}
              </div>
              <div>
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block leading-none mb-1">Active Section</span>
                <span className="text-sm font-black text-slate-800 uppercase tracking-tight leading-none">{activeTab}</span>
              </div>
            </div>
            <button
              onClick={() => setIsNavMenuOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl shadow-sm text-[10px] font-black uppercase tracking-widest text-slate-600"
            >
              Menu <ChevronDown className="w-3 h-3" />
            </button>
          </div>
          </>}

          <div className="p-3 sm:p-8 lg:p-12">
            {/* DASHBOARD */}
            {activeTab === 'dashboard' && (
              <div className="space-y-12 animate-fade-in">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                  {stats.map((stat, idx) => (
                    <div key={idx} className="bg-white p-6 lg:p-8 border rounded-[2rem] lg:rounded-3xl shadow-sm hover:shadow-xl transition-all border-slate-100 flex flex-col justify-between">
                      <div className="text-slate-400 text-[9px] lg:text-[10px] font-black uppercase tracking-widest mb-2 lg:mb-4">{stat.label}</div>
                      <div className="text-2xl lg:text-4xl font-black text-blue-600 leading-none">{stat.value}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-6">
                  <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-3 text-slate-400">
                    <Clock className="w-4 h-4" /> Recent Activity
                  </h2>
                  {materials.length === 0 ? (
                    <div className="text-center py-20 bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                      <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No activity recorded</p>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      {materials.map(m => (
                        <div key={m._id} className="flex justify-between items-center p-6 bg-white rounded-2xl hover:shadow-lg transition-all border border-slate-100">
                          <div>
                            <h3 className="font-black text-slate-800">{m.title}</h3>
                            <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-wider">
                              {m.course?.title || m.course} • {m.downloads || 0} downloads • <span className="text-blue-500">{m.status}</span>
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handlePreview(m._id)} className="p-3 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><Eye className="w-5 h-5" /></button>
                            <button onClick={() => handleDownload(m._id)} className="p-3 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-xl transition-all"><Download className="w-5 h-5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* LIBRARY */}
            {activeTab === 'materials' && (
              <div className="space-y-8 animate-fade-in">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-black uppercase tracking-widest">Registry Library</h2>
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{materials.length} Total Assets</div>
                </div>
                {materials.length === 0 ? (
                  <p className="text-center py-20 text-slate-400 font-bold uppercase tracking-widest">No materials in public library</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {materials.map(m => (
                      <div key={m._id} className="bg-white border border-slate-100 rounded-[2.5rem] p-8 flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all group">
                        <div>
                          <div className="flex justify-between items-start mb-4">
                            <div className="p-3 bg-blue-50 rounded-2xl group-hover:bg-blue-600 transition-colors">
                              <BookOpen className="w-5 h-5 text-blue-600 group-hover:text-white" />
                            </div>
                            <span className={`px-4 py-1.5 rounded-full text-[8px] font-black uppercase tracking-widest ${m.status === 'approved' ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
                              {m.status}
                            </span>
                          </div>
                          <h3 className="font-black text-slate-800 text-lg leading-tight mb-2 line-clamp-2">{m.title}</h3>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">{m.course?.title || "General Resource"}</p>
                        </div>

                        <div className="space-y-6">
                          <div className="flex items-center gap-6 pt-6 border-t border-slate-50">
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Impact</span>
                              <span className="text-xs font-black text-slate-700">{m.downloads || 0} hits</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest mb-1">Volume</span>
                              <span className="text-xs font-black text-slate-700">{m.file?.size ? `${Math.round(m.file.size / 1024)} KB` : '---'}</span>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button onClick={() => handlePreview(m._id)} className="flex-1 py-4 bg-slate-50 text-slate-500 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-blue-50 hover:text-blue-600 transition-all flex items-center justify-center gap-2">
                              <Eye className="w-4 h-4" /> View
                            </button>
                            <button onClick={() => handleMaterialDelete(m._id)} className="p-4 bg-red-50 text-red-600 rounded-2xl hover:bg-red-600 hover:text-white transition-all flex items-center justify-center">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* UPLOAD */}
            {activeTab === 'upload' && (
              <div className="max-w-4xl mx-auto animate-fade-in">
                <form onSubmit={handleUploadSubmit} className="space-y-8 bg-white p-10 border rounded-[3rem] shadow-sm border-slate-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Department</label>
                      <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" required>
                        <option value="">Select Dept</option>
                        {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Academic Year</label>
                      <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase focus:ring-2 focus:ring-blue-500 transition-all shadow-inner disabled:opacity-30" disabled={!selectedDept} required>
                        <option value="">Select Year</option>
                        {availableYears.map(y => <option key={y} value={y}>Year {y}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Target Course</label>
                    <select value={uploadForm.course} onChange={e => setUploadForm({ ...uploadForm, course: e.target.value })} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase focus:ring-2 focus:ring-blue-500 transition-all shadow-inner disabled:opacity-30" disabled={!selectedYear} required>
                      <option value="">Select Course</option>
                      {courses.map(c => <option key={c._id} value={c._id}>{c.courseCode} - {c.title}</option>)}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Material Title</label>
                    <input type="text" value={uploadForm.title} onChange={e => setUploadForm({ ...uploadForm, title: e.target.value })} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" required placeholder="TITLE..." />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Description</label>
                    <textarea rows="3" value={uploadForm.description} onChange={e => setUploadForm({ ...uploadForm, description: e.target.value })} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase focus:ring-2 focus:ring-blue-500 transition-all shadow-inner" placeholder="OVERVIEW..." />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest pl-2">Asset Selection</label>
                    <div className="relative border-2 border-dashed border-slate-200 rounded-[2.5rem] p-12 text-center bg-slate-50 hover:bg-blue-50 hover:border-blue-200 transition-all group cursor-pointer">
                      <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleFileChange} required />
                      <Upload className="w-10 h-10 text-slate-300 mx-auto mb-4 group-hover:text-blue-400 transition-colors" />
                      <span className="text-blue-600 font-black text-[10px] uppercase tracking-widest block mb-1">{uploadForm.file ? uploadForm.file.name : 'Click to Upload Asset'}</span>
                      <span className="text-[8px] text-slate-400 font-bold uppercase tracking-[0.2em]">MAX 30MB • PDF, WORD, PPT, IMAGE</span>
                    </div>
                  </div>

                  <Button type="submit" variant="primary" disabled={loading} fullWidth className="py-6 rounded-2xl text-[11px] font-black uppercase tracking-[0.3em] shadow-xl shadow-blue-500/20">
                    {loading ? 'Initializing...' : 'Publish to Registry'}
                  </Button>
                </form>
              </div>
            )}

            {/* CHAT / MESSAGING */}
            {activeTab === 'chat' && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden h-[calc(100vh-230px)] md:h-[650px] min-h-[460px] max-h-[700px] flex animate-fade-in">
                {/* Contacts List Column */}
                <div className={`w-full md:w-[350px] border-r border-slate-100 flex flex-col h-full bg-slate-50/50 ${selectedContact ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-6 border-b border-slate-100 bg-white">
                    <h3 className="text-sm font-black uppercase tracking-widest text-slate-800 mb-4">Inbox</h3>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search colleagues..."
                        className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200/60 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                        value={chatSearch}
                        onChange={e => setChatSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
                    {loadingChat ? (
                      <div className="flex flex-col items-center justify-center py-20">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).length === 0 ? (
                      <div className="text-center py-12 text-slate-400 text-xs font-bold uppercase tracking-wider">No contacts found</div>
                    ) : (
                      contacts.filter(c => c.fullName?.toLowerCase().includes(chatSearch.toLowerCase())).map(contact => {
                        const isSelected = selectedContact?._id === contact._id;
                        return (
                          <button
                            key={contact._id}
                            onClick={() => handleSelectContact(contact)}
                            className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left ${isSelected ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white hover:bg-slate-100 border border-slate-100/50'}`}
                          >
                            <div className="relative flex-shrink-0">
                              <div className="w-12 h-12 rounded-full overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                                {contact.profileImage && (typeof contact.profileImage === 'string' || contact.profileImage.url) ? (
                                  <img src={publicAPI.getFullImageUrl(typeof contact.profileImage === 'string' ? contact.profileImage : contact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                                ) : (
                                  <span className={`text-lg font-black uppercase ${isSelected ? 'text-blue-600' : 'text-slate-400'}`}>
                                    {contact.fullName?.charAt(0)}
                                  </span>
                                )}
                              </div>
                              <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${contact.role === 'admin' ? 'bg-purple-500' : 'bg-green-500'}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-baseline mb-1">
                                <h4 className={`text-xs font-black truncate uppercase tracking-tight ${isSelected ? 'text-white' : 'text-slate-800'}`}>{contact.fullName}</h4>
                                <span className={`text-[8px] font-bold ${isSelected ? 'text-blue-200' : 'text-slate-400'}`}>
                                  {contact.lastMessage ? new Date(contact.lastMessage.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className={`text-[10px] truncate ${isSelected ? 'text-blue-100' : 'text-slate-500'} max-w-[160px]`}>
                                  {contact.lastMessage ? contact.lastMessage.content : 'No messages yet'}
                                </p>
                                {contact.unreadCount > 0 && (
                                  <span className="bg-red-500 text-white text-[8px] font-black w-4.5 h-4.5 rounded-full flex items-center justify-center border border-white px-1">
                                    {contact.unreadCount}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Messages Chat Stream Column */}
                <div className={`flex-1 flex flex-col h-full bg-white ${!selectedContact ? 'hidden md:flex items-center justify-center' : 'flex'}`}>
                  {selectedContact ? (
                    <>
                      {/* Active Contact Header */}
                      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/20">
                        <div className="flex items-center gap-4">
                          <button onClick={() => setSelectedContact(null)} className="md:hidden p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5" /></button>
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                            {selectedContact.profileImage && (typeof selectedContact.profileImage === 'string' || selectedContact.profileImage.url) ? (
                              <img src={publicAPI.getFullImageUrl(typeof selectedContact.profileImage === 'string' ? selectedContact.profileImage : selectedContact.profileImage.url)} className="w-full h-full object-cover" alt="" />
                            ) : (
                              <span className="text-lg font-black text-slate-400 uppercase">
                                {selectedContact.fullName?.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div>
                            <h4 className="text-xs font-black text-slate-800 uppercase tracking-tight">{selectedContact.fullName}</h4>
                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{selectedContact.role?.replace('_', ' ')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Message History bubble container */}
                      <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar bg-slate-50/20">
                        {loadingMessages ? (
                          <div className="flex items-center justify-center py-20">
                            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <MessageSquare className="w-10 h-10 mb-2 opacity-30" />
                            <p className="text-[10px] font-black uppercase tracking-widest">Say hello to {selectedContact.fullName}!</p>
                          </div>
                        ) : (
                          messages.map((msg, i) => {
                            const isMe = msg.sender === teacher?._id || msg.sender?._id === teacher?._id || msg.sender === teacher?.id;
                            return (
                              <div key={msg._id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'} animate-fade-in`}>
                                <div className={`max-w-[70%] p-4 rounded-2xl shadow-sm text-xs font-semibold leading-relaxed ${isMe ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none border border-slate-200/50'}`}>
                                  {msg.attachment?.url && (
                                    msg.attachment.fileType === 'image' ? (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer">
                                        <img src={publicAPI.getFullImageUrl(msg.attachment.url)} alt={msg.attachment.originalName} className="rounded-xl max-w-full max-h-48 object-cover mb-2 border border-white/20" />
                                      </a>
                                    ) : (
                                      <a href={publicAPI.getFullImageUrl(msg.attachment.url)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 p-2.5 rounded-xl mb-2 ${isMe ? 'bg-white/15 hover:bg-white/25' : 'bg-white hover:bg-slate-50 border border-slate-200'} transition-colors`}>
                                        <FileText className="w-5 h-5 flex-shrink-0" />
                                        <span className="truncate text-[10px] font-bold">{msg.attachment.originalName || 'Download File'}</span>
                                        <Download className="w-4 h-4 flex-shrink-0 ml-auto" />
                                      </a>
                                    )
                                  )}
                                  {msg.content && <p>{msg.content}</p>}
                                  <div className="flex justify-end items-center gap-1 mt-1 text-[8px] opacity-70">
                                    <span>{new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isMe && (
                                      <CheckCheck className={`w-3.5 h-3.5 ${msg.read ? 'text-blue-200' : 'text-slate-300'}`} />
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* File Preview Chip */}
                      {chatAttachment && (
                        <div className="px-4 pt-3 pb-0 bg-white border-t border-slate-100">
                          <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs font-bold text-blue-700">
                            <Paperclip className="w-3.5 h-3.5" />
                            <span className="truncate flex-1">{chatAttachment.name}</span>
                            <button type="button" onClick={() => setChatAttachment(null)} className="p-0.5 hover:bg-blue-100 rounded-full"><X className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      )}
                      {/* Message Send Form Footer */}
                      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 flex gap-3 bg-white">
                        <input type="file" ref={chatFileInputRef} className="hidden" onChange={e => { if (e.target.files[0]) setChatAttachment(e.target.files[0]); e.target.value = ''; }} />
                        <button
                          type="button"
                          onClick={() => chatFileInputRef.current?.click()}
                          className="p-3.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all flex items-center justify-center"
                          title="Attach a file"
                        >
                          <Paperclip className="w-5 h-5" />
                        </button>
                        <input
                          type="text"
                          placeholder="Type your message here..."
                          className="flex-1 px-4 py-3.5 bg-slate-50 border border-slate-200/60 rounded-2xl text-xs font-bold outline-none focus:ring-2 focus:ring-blue-500 shadow-inner"
                          value={newMessage}
                          onChange={e => setNewMessage(e.target.value)}
                        />
                        <button
                          type="submit"
                          disabled={sendingMsg || (!newMessage.trim() && !chatAttachment)}
                          className="p-3.5 bg-blue-600 text-white rounded-2xl shadow-lg shadow-blue-500/20 hover:bg-blue-700 transition-all flex items-center justify-center disabled:opacity-50"
                        >
                          <Send className="w-5 h-5" />
                        </button>
                      </form>
                    </>
                  ) : (
                    <div className="text-center p-8 space-y-4">
                      <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-[2rem] flex items-center justify-center mx-auto shadow-inner">
                        <MessageSquare className="w-10 h-10" />
                      </div>
                      <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-800">Secure Direct Messaging</h4>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest max-w-sm leading-relaxed">Select an administrator or colleague profile from the list to start a confidential dialogue.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PROFILE / SETTINGS */}
            {activeTab === 'profile' && (
              <div className="max-w-5xl mx-auto space-y-8 animate-fade-in">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Left: Info */}
                  <div className="lg:col-span-1 space-y-8">
                    <div className="bg-white p-6 md:p-10 border rounded-[2rem] md:rounded-[3rem] shadow-sm border-slate-100 text-center">
                      <div className="relative w-32 h-32 mx-auto mb-6 group">
                        <div className="w-full h-full bg-slate-100 rounded-full border-4 border-white shadow-xl overflow-hidden">
                          {profileData.profileImage ? (
                            <img src={profileData.profileImage} className="w-full h-full object-cover" alt="" key={profileData.profileImage} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300"><User className="w-12 h-12" /></div>
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full cursor-pointer hover:bg-blue-500 transition-all border-2 border-white shadow-lg">
                          <Camera className="w-4 h-4 text-white" />
                          <input type="file" className="hidden" accept="image/*" onChange={handleAvatarChange} />
                        </label>
                      </div>
                      <h3 className="font-black text-lg text-slate-800">{profileData.fullName}</h3>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 break-all">{profileData.email}</p>
                    </div>

                    <div className="bg-blue-600 p-6 md:p-8 rounded-[2rem] md:rounded-[3rem] text-white shadow-xl shadow-blue-600/20">
                      <Shield className="w-8 h-8 mb-4 opacity-50" />
                      <h4 className="font-black text-sm uppercase tracking-widest mb-2">Verified Profile</h4>
                      <p className="text-[10px] opacity-80 leading-relaxed font-bold uppercase tracking-wider">Your account is fully synchronized with the {profileData.school} registry.</p>
                    </div>
                  </div>

                  {/* Right: Forms */}
                  <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white p-6 md:p-10 border rounded-[2rem] md:rounded-[3rem] shadow-sm border-slate-100">
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
                        <User className="w-4 h-4" /> Identity Details
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-5 bg-slate-50 rounded-2xl">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">School Affiliation</label>
                          <div className="text-[11px] font-black text-slate-800 uppercase tracking-widest leading-relaxed">{profileData.school}</div>
                        </div>
                        <div className="p-5 bg-slate-50 rounded-2xl">
                          <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Primary Department</label>
                          <div className="text-[11px] font-black text-slate-800 uppercase tracking-widest leading-relaxed">{profileData.department}</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 md:p-10 border rounded-[2rem] md:rounded-[3rem] shadow-sm border-slate-100 overflow-hidden">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 pb-8 border-b border-slate-50 gap-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-xl flex-shrink-0 ${profileData.showAvatarPublicly ? 'bg-green-50' : 'bg-amber-50'}`}>
                            {profileData.showAvatarPublicly ? <Globe className="w-5 h-5 text-green-600" /> : <EyeOff className="w-5 h-5 text-amber-600" />}
                          </div>
                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Public Visibility</h4>
                            <p className="text-[11px] font-bold text-slate-800 uppercase mt-0.5">{profileData.showAvatarPublicly ? 'Avatar Visible to Students' : 'Avatar Hidden from Students'}</p>
                          </div>
                        </div>
                        <button
                          onClick={toggleVisibility}
                          className={`w-full sm:w-auto px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${profileData.showAvatarPublicly ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-green-500 text-white hover:bg-green-600'}`}
                        >
                          {profileData.showAvatarPublicly ? 'Hide Avatar' : 'Show Avatar'}
                        </button>
                      </div>

                      {!isChangingPassword ? (
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-slate-50 rounded-xl flex-shrink-0"><Lock className="w-5 h-5 text-slate-400" /></div>
                            <div>
                              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Security Protocol</h4>
                              <p className="text-[11px] font-bold text-slate-800 uppercase mt-0.5">Account Key Configuration</p>
                            </div>
                          </div>
                          <button
                            onClick={() => setIsChangingPassword(true)}
                            className="w-full sm:w-auto px-6 py-3.5 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2"
                          >
                            <Key className="w-3.5 h-3.5" /> Change Password
                          </button>
                        </div>
                      ) : (
                        <div className="animate-fade-in">
                          <div className="flex items-center justify-between mb-8">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                              <Lock className="w-4 h-4" /> Security Protocol
                            </h4>
                            <button onClick={() => setIsChangingPassword(false)} className="text-[9px] font-black uppercase tracking-widest text-slate-300 hover:text-slate-500 transition-colors">Discard</button>
                          </div>
                          <form onSubmit={handlePasswordChange} className="space-y-4">
                            <input
                              type="password"
                              placeholder="CURRENT PASSWORD"
                              value={passwordForm.currentPassword}
                              onChange={e => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                              className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase tracking-widest focus:ring-2 focus:ring-blue-500 shadow-inner"
                              required
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <input
                                type="password"
                                placeholder="NEW PASSWORD"
                                value={passwordForm.newPassword}
                                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase tracking-widest focus:ring-2 focus:ring-blue-500 shadow-inner"
                                required
                              />
                              <input
                                type="password"
                                placeholder="CONFIRM NEW"
                                value={passwordForm.confirmPassword}
                                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                className="w-full p-4 bg-slate-50 border-none rounded-2xl font-bold text-xs uppercase tracking-widest focus:ring-2 focus:ring-blue-500 shadow-inner"
                                required
                              />
                            </div>
                            <Button type="submit" variant="primary" className="w-full py-5 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-blue-500/20">Update Security Key</Button>
                          </form>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* VAULT */}
            {activeTab === 'vault' && (
              <div className="space-y-6 animate-fade-in relative h-full">
                {previewItem ? (
                  <div className="space-y-6 animate-fade-in">
                    <div className="flex items-center justify-between bg-slate-50 p-4 lg:p-6 rounded-[2rem] border border-slate-100">
                      <button onClick={handleGoBack} className="p-3 bg-white rounded-2xl text-slate-600 hover:text-blue-600 transition-all border border-slate-100 shadow-sm"><ArrowLeft className="w-5 h-5" /></button>
                      <h2 className="font-black text-sm uppercase tracking-widest truncate max-w-[200px]">{previewItem.title}</h2>
                      <div className="flex gap-2">
                        {(() => {
                          const ext = previewItem.file?.format?.toLowerCase();
                          if (ext === 'html' || ext === 'txt') {
                            return (
                              <button 
                                onClick={() => openNoteForEditing(previewItem)} 
                                className="p-3 bg-indigo-600 text-white rounded-xl shadow-lg flex items-center gap-2 font-bold text-[10px] uppercase tracking-wider hover:bg-indigo-700 transition-all"
                                title="Edit Note"
                              >
                                <Edit3 className="w-4 h-4" /> <span className="hidden sm:inline">Edit Note</span>
                              </button>
                            );
                          }
                          return null;
                        })()}
                        <button onClick={() => window.open(personalStorageAPI.downloadFile(previewItem._id), '_blank')} className="p-3 bg-blue-600 text-white rounded-xl shadow-lg"><Download className="w-4 h-4" /></button>
                        <button onClick={() => setPreviewItem(null)} className="p-3 bg-slate-200 text-slate-600 rounded-xl"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="bg-slate-900 rounded-[2.5rem] overflow-hidden aspect-[4/3] lg:aspect-video flex items-center justify-center relative shadow-2xl">
                      {['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(previewItem.file?.format?.toLowerCase()) ? (
                        <img src={personalStorageAPI.previewFile(previewItem._id)} className="w-full h-full object-contain" alt="" />
                      ) : ['pdf', 'html'].includes(previewItem.file?.format?.toLowerCase()) ? (
                        <iframe src={personalStorageAPI.previewFile(previewItem._id)} className="w-full h-full border-none bg-white" title="Document Preview" />
                      ) : (
                        <div className="text-center space-y-6">
                          <FileText className="w-24 h-24 text-slate-700 mx-auto fill-current opacity-20" />
                          <p className="text-slate-400 font-black uppercase tracking-[0.3em] text-xs">No Preview Available</p>
                          <Button onClick={() => window.open(personalStorageAPI.downloadFile(previewItem._id), '_blank')} variant="primary" className="px-10 py-4 rounded-2xl">Download Asset</Button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="flex justify-between items-center bg-slate-50 p-4 lg:p-6 rounded-[2rem] border border-slate-100">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => setIsVaultNavOpen(true)}
                          className="lg:hidden p-3 bg-white rounded-xl text-slate-600 border border-slate-100 shadow-sm"
                        >
                          <Menu className="w-4 h-4" />
                        </button>
                        <div className="p-3 bg-blue-600 text-white rounded-xl shadow-lg hidden lg:block"><Lock className="w-5 h-5" /></div>
                        <div><h2 className="text-lg font-black tracking-tight uppercase">{vaultCategory === 'all' ? 'Universal Vault' : vaultCategory}</h2></div>
                      </div>
                      <div className="flex items-center gap-3">
                        {clipboard && <button onClick={handlePaste} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-black text-[9px] uppercase tracking-widest shadow-xl animate-pulse"><Clipboard className="w-4 h-4" /> Paste</button>}
                        <button 
                          onClick={openNewNoteEditor}
                          className="flex items-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-indigo-600/20 hover:bg-indigo-700"
                        >
                          <Edit3 className="w-4 h-4" /> Write Note
                        </button>
                        <button onClick={() => document.getElementById('v-upload').click()} className="p-3 bg-blue-600 text-white rounded-xl"><Plus className="w-5 h-5" /></button>
                        <input type="file" id="v-upload" className="hidden" onChange={handleVaultUpload} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                      {/* Mobile Vault Category Drawer */}
                      {isVaultNavOpen && (
                        <div className="fixed inset-0 z-[11000] flex lg:hidden animate-fade-in">
                          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsVaultNavOpen(false)} />
                          <div className="relative w-64 bg-white h-full shadow-2xl flex flex-col animate-slide-right mt-16 md:mt-20">
                            <div className="p-6 bg-slate-50 border-b flex items-center justify-between">
                              <span className="font-black text-[10px] uppercase tracking-widest text-slate-400">Filter Vault</span>
                              <button onClick={() => setIsVaultNavOpen(false)} className="p-2 bg-white rounded-lg text-slate-400"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                              {[
                                { id: 'all', label: 'All Files', icon: LayoutGrid },
                                { id: 'image', label: 'Photos', icon: Eye },
                                { id: 'document', label: 'Documents', icon: FileText },
                                { id: 'archive', label: 'Archives', icon: HardDrive },
                                { id: 'other', label: 'Others', icon: ShieldCheck }
                              ].map(cat => (
                                <button
                                  key={cat.id}
                                  onClick={() => { handleCategorySelect(cat.id); setMobileVaultStep('files'); setIsVaultNavOpen(false); }}
                                  className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all ${vaultCategory === cat.id ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-slate-500 hover:bg-slate-50'}`}
                                >
                                  <cat.icon className={`w-4 h-4 ${vaultCategory === cat.id ? 'text-white' : 'text-slate-400'}`} />
                                  <span className="text-[10px] font-black uppercase tracking-widest">{cat.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Desktop Categories Sidebar */}
                      <div className="hidden lg:block lg:col-span-3 space-y-4 animate-fade-in pl-4">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6">Explore</h4>
                        {[
                          { id: 'all', label: 'All Files', icon: LayoutGrid },
                          { id: 'image', label: 'Photos', icon: Eye },
                          { id: 'document', label: 'Documents', icon: FileText },
                          { id: 'archive', label: 'Archives', icon: HardDrive },
                          { id: 'other', label: 'Others', icon: ShieldCheck }
                        ].map(cat => (
                          <button key={cat.id} onClick={() => handleCategorySelect(cat.id)} className={`w-full flex items-center gap-4 py-2 transition-colors group ${vaultCategory === cat.id ? 'text-blue-600 font-black' : 'text-slate-500 hover:text-blue-600 font-bold'}`}>
                            <cat.icon className={`w-4 h-4 ${vaultCategory === cat.id ? 'text-blue-600' : 'text-slate-400 group-hover:text-blue-600'}`} />
                            <span className="text-[11px] uppercase tracking-widest">{cat.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="lg:col-span-9 bg-white p-4 lg:p-8 rounded-[2.5rem] border border-slate-100 min-h-[600px] animate-fade-in">
                        <div className="flex items-center gap-4 mb-8">
                          <button onClick={handleGoBack} className="p-3 bg-slate-50 rounded-2xl text-slate-600 hover:text-blue-600 transition-all border border-slate-100"><ArrowLeft className="w-5 h-5" /></button>
                          <div className="flex-1 overflow-hidden">
                            <div className="flex items-center gap-2 font-black text-slate-400 text-[9px] uppercase tracking-widest truncate">VAULT <ChevronRight className="w-3 h-3 opacity-30" /> <span className="text-blue-600">{currentFolder ? folderPath[folderPath.length - 1]?.name : vaultCategory}</span></div>
                          </div>
                          <button onClick={() => { setTargetCategoryForNewFolder(vaultCategory); setIsNewFolderModalOpen(true); }} className="p-3 bg-slate-50 text-blue-600 rounded-2xl border border-slate-100 hover:bg-blue-600 hover:text-white transition-all"><FolderPlus className="w-5 h-5" /></button>
                        </div>
                        {vaultLoading ? (
                          <div className="flex flex-col items-center justify-center py-40"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
                        ) : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                            {vaultFolders.map(folder => (
                              <div key={folder._id} onDoubleClick={() => navigateToFolder(folder)} className="group relative flex flex-col items-center gap-2 p-4 transition-all cursor-pointer">
                                <div className="w-20 h-20 bg-blue-50 text-blue-500 rounded-[2rem] flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all"><Folder className="w-10 h-10 fill-current opacity-40" /></div>
                                <h5 className={`font-bold text-[10px] uppercase tracking-tighter text-center w-full transition-colors ${activeMenuId === folder._id ? 'text-blue-600' : 'text-slate-600 group-hover:text-blue-600'}`}>{folder.name}</h5>
                                <button onClick={(e) => { e.stopPropagation(); setActiveMenuId(activeMenuId === folder._id ? null : folder._id); }} className="p-1 text-black"><MoreHorizontal className="w-5 h-5" /></button>
                              </div>
                            ))}
                            {vaultFiles.map(file => {
                              const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(file.file?.format?.toLowerCase());
                              return (
                                <div key={file._id} onClick={() => openPreview(file)} className="group relative flex flex-col bg-white border border-slate-100 overflow-hidden hover:shadow-xl transition-all cursor-pointer rounded-none">
                                  <div className={`aspect-square relative overflow-hidden bg-slate-50 ${isImage ? '' : 'flex items-center justify-center'}`}>
                                    {isImage ? (<img src={personalStorageAPI.previewFile(file._id)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" alt="" />) : (
                                      <div className="text-center space-y-4"><FileText className="w-12 h-12 text-slate-200 fill-current opacity-50 mx-auto" /><span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">{file.file?.format}</span></div>
                                    )}
                                    {!isImage && (
                                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-white/90 backdrop-blur-md border-t border-slate-100">
                                        <h6 className="font-bold text-[9px] truncate uppercase tracking-widest text-slate-700 group-hover:text-blue-600 transition-colors">{file.title}</h6>
                                      </div>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); setActiveMenuId(activeMenuId === file._id ? null : file._id); }} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center text-black"><MoreHorizontal className="w-5 h-5" /></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ASSET PROPERTIES MODAL */}
      {showInfoItem && (
        <div className="fixed inset-0 z-[5000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-md" onClick={() => setShowInfoItem(null)} />
          <div className="relative bg-white w-full max-w-md p-10 shadow-2xl animate-scale-up rounded-[2.5rem]">
            <h3 className="text-xl font-black mb-8 uppercase tracking-widest">Asset Details</h3>
            <div className="space-y-6">
              {[
                { label: 'Name', value: showInfoItem.title || showInfoItem.name },
                { label: 'Type', value: showInfoItem.file?.format?.toUpperCase() || 'Folder' },
                { label: 'Size', value: showInfoItem.file?.size ? `${Math.round(showInfoItem.file.size / 1024)} KB` : '---' },
                { label: 'Date', value: new Date(showInfoItem.createdAt).toLocaleString() }
              ].map((item, idx) => (
                <div key={idx} className="flex justify-between items-center border-b border-slate-50 pb-2">
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{item.label}</span>
                  <span className="text-[10px] font-black text-slate-900">{item.value}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowInfoItem(null)} className="w-full mt-10 py-4 bg-blue-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest">Close</button>
          </div>
        </div>
      )}

      {/* GLOBAL MANAGEMENT SHEET */}
      {activeMenuId && (
        <div className="fixed inset-0 z-[3000] flex items-end lg:items-center lg:justify-center p-0 lg:p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setActiveMenuId(null)} />
          <div className={`bottom-sheet relative bg-white w-full lg:w-72 rounded-t-[2.5rem] lg:rounded-[2.5rem] p-8 shadow-2xl animate-slide-up lg:animate-scale-up border-t lg:border border-slate-100`}>
            <h3 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-6 text-center">Manage</h3>
            <div className="space-y-2">
              {vaultFolders.find(f => f._id === activeMenuId) ? (
                <>
                  <button onClick={() => { const f = vaultFolders.find(f => f._id === activeMenuId); setShowInfoItem(f); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Info className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Info</span></button>
                  <button onClick={() => { const f = vaultFolders.find(f => f._id === activeMenuId); handleCopy(f, 'folder'); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Copy className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Copy</span></button>
                  <button onClick={() => { const f = vaultFolders.find(f => f._id === activeMenuId); setSelectedItemForMove({ ...f, type: 'folder' }); setIsMoveModalOpen(true); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Move className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Move</span></button>
                  <button onClick={() => { if (window.confirm('Delete?')) personalStorageAPI.deleteFolder(activeMenuId).then(() => loadFolders(currentFolder, vaultCategory)); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-red-50 text-red-600 rounded-2xl flex items-center gap-4 transition-all border-t border-slate-50"><Trash2 className="w-4 h-4" /><span className="font-black text-[10px] uppercase tracking-widest">Delete</span></button>
                </>
              ) : (
                <>
                  <button onClick={() => { const f = vaultFiles.find(f => f._id === activeMenuId); setShowInfoItem(f); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Info className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Info</span></button>
                  {(() => {
                    const f = vaultFiles.find(f => f._id === activeMenuId);
                    const ext = f?.file?.format?.toLowerCase();
                    if (ext === 'html' || ext === 'txt') {
                      return (
                        <button 
                          onClick={() => { openNoteForEditing(f); setActiveMenuId(null); }} 
                          className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"
                        >
                          <Edit3 className="w-4 h-4 text-blue-500" />
                          <span className="font-black text-[10px] uppercase tracking-widest">Edit Note</span>
                        </button>
                      );
                    }
                    return null;
                  })()}
                  <button onClick={() => { const f = vaultFiles.find(f => f._id === activeMenuId); openPreview(f); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Eye className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">View</span></button>
                  <button onClick={() => { window.open(personalStorageAPI.downloadFile(activeMenuId), '_blank'); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Download className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Download</span></button>
                  <button onClick={() => handleShare(activeMenuId)} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Share2 className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Share</span></button>
                  <button onClick={() => { const f = vaultFiles.find(f => f._id === activeMenuId); handleCopy(f, 'file'); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Copy className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Copy</span></button>
                  <button onClick={() => { const f = vaultFiles.find(f => f._id === activeMenuId); setSelectedItemForMove({ ...f, type: 'file' }); setIsMoveModalOpen(true); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-slate-50 rounded-2xl flex items-center gap-4 transition-all"><Move className="w-4 h-4 text-blue-500" /><span className="font-black text-[10px] uppercase tracking-widest">Move</span></button>
                  <button onClick={() => { if (window.confirm('Delete?')) personalStorageAPI.deleteFile(activeMenuId).then(() => loadVaultFiles()); setActiveMenuId(null); }} className="w-full text-left p-4 hover:bg-red-50 text-red-600 rounded-2xl flex items-center gap-4 transition-all border-t border-slate-50"><Trash2 className="w-4 h-4" /><span className="font-black text-[10px] uppercase tracking-widest">Destroy</span></button>
                </>
              )}
            </div>
            <button onClick={() => setActiveMenuId(null)} className="w-full mt-6 py-4 bg-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-400">Dismiss</button>
          </div>
        </div>
      )}

      {/* Relocation Modal */}
      {isMoveModalOpen && (
        <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-xl z-[4000] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-10 shadow-2xl animate-scale-up">
            <h3 className="text-xl font-black mb-1 uppercase tracking-widest">Relocate</h3>
            <div className="space-y-2 mt-8 mb-10 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
              <button onClick={() => handleMoveItem(null)} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-slate-50 hover:bg-blue-600 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest"><LayoutGrid className="w-4 h-4" /> Vault Root</button>
              {vaultFolders.filter(f => f._id !== selectedItemForMove?._id).map(f => (
                <button key={f._id} onClick={() => handleMoveItem(f._id)} className="w-full flex items-center gap-4 p-5 rounded-2xl bg-slate-50 hover:bg-blue-600 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest"><Folder className="w-4 h-4" /> {f.name}</button>
              ))}
            </div>
            <button onClick={() => setIsMoveModalOpen(false)} className="w-full py-4 font-black text-[10px] uppercase tracking-widest text-slate-300">Cancel</button>
          </div>
        </div>
      )}

      {/* FOLDER MODAL */}
      {isNewFolderModalOpen && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[4000] flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-10 shadow-2xl animate-scale-up">
            <h3 className="text-xl font-black mb-1 uppercase tracking-widest">New Folder</h3>
            <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="NAME..." className="w-full p-5 bg-slate-50 rounded-2xl border-none font-black text-sm uppercase my-8 outline-none shadow-inner" autoFocus />
            <div className="flex gap-4"><button onClick={() => setIsNewFolderModalOpen(false)} className="flex-1 font-black text-[10px] uppercase tracking-widest text-slate-300">Abort</button><button onClick={handleCreateFolder} className="flex-1 py-5 bg-blue-600 text-white rounded-2xl font-black shadow-xl uppercase text-[10px] tracking-widest">Create</button></div>
          </div>
        </div>
      )}

      {/* MOBILE NAV SIDEBAR */}
      {isNavMenuOpen && (
        <div className="fixed inset-0 z-[6000] flex lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsNavMenuOpen(false)}
          />
          <div className="relative bg-white w-[75%] md:w-[50%] h-full shadow-2xl animate-slide-right flex flex-col mt-16 md:mt-20 lg:mt-0">
            <div className="p-8 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
              <div className="flex justify-between items-center mb-6">
                <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
                <button onClick={() => setIsNavMenuOpen(false)} className="p-2 hover:bg-white/10 rounded-lg">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <h3 className="font-black text-lg leading-tight">{teacher.fullName}</h3>
              <p className="text-[10px] font-bold text-blue-100 uppercase tracking-widest mt-1 opacity-80">{profileData.department}</p>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-2">
              <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-2">Navigation</h4>
              {[
                { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                { id: 'materials', label: 'Library', icon: BookOpen },
                { id: 'upload', label: 'Publish', icon: Upload },
                { id: 'chat', label: 'Colleague Chat', icon: MessageSquare, badge: totalUnreadChat },
                { id: 'vault', label: 'Private Vault', icon: Lock },
                { id: 'profile', label: 'My Profile', icon: User }
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => { setActiveTab(t.id); if (t.id === 'vault') setMobileVaultStep('categories'); setPreviewItem(null); setIsNavMenuOpen(false); }}
                  className={`flex items-center gap-4 w-full p-4 rounded-2xl transition-all relative ${activeTab === t.id ? 'bg-blue-50 text-blue-600 font-black shadow-sm' : 'text-slate-500 hover:bg-slate-50 font-bold'}`}
                >
                  <div className={`p-2 rounded-xl ${activeTab === t.id ? 'bg-blue-600 text-white' : 'bg-white shadow-sm'}`}>
                    <t.icon className="w-5 h-5" />
                  </div>
                  <span className="text-[11px] uppercase tracking-widest">{t.label}</span>
                  {t.badge > 0 && (
                    <span className="absolute right-4 bg-red-500 text-white text-[9px] font-black w-5.5 h-5.5 rounded-full flex items-center justify-center border-2 border-white animate-pulse px-1">
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="p-6 border-t border-slate-100">
              <button
                onClick={handleLogout}
                className="flex items-center gap-4 w-full p-4 rounded-2xl bg-red-50 text-red-600 font-black text-[11px] uppercase tracking-widest transition-all hover:bg-red-100"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTE EDITOR OVERLAY */}
      {isNoteEditorOpen && (
        <div className="fixed inset-0 z-[12000] bg-slate-900/60 backdrop-blur-md flex justify-end animate-fade-in">
          <div className="bg-white w-full max-w-4xl h-full shadow-2xl flex flex-col animate-slide-left p-6 md:p-8">
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-slate-100 mb-6">
              <div className="flex items-center gap-4">
                <button 
                  onClick={closeNoteEditor} 
                  className="p-3 bg-slate-50 hover:bg-slate-100 rounded-2xl text-slate-600 transition-all border border-slate-100"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-lg font-black uppercase tracking-wider text-slate-900">
                    {editingNoteId ? 'Modify Note' : 'Write Styled Note'}
                  </h2>
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    Draft beautifully & publish to PDF, DOCX, HTML, or PPT
                  </p>
                </div>
              </div>
              <div className="relative flex items-center gap-3">
                <button 
                  onClick={() => setInsertLinkModalOpen(true)} 
                  className="flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-slate-100 transition-all"
                  title="Link Vault File"
                >
                  <Paperclip className="w-4 h-4 text-blue-500" /> Link Vault Asset
                </button>
                <div className="relative">
                  <button 
                    onClick={toggleSaveDropdown} 
                    title="Save Note"
                    className="flex items-center justify-center w-11 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-xl shadow-blue-500/10 group transition-colors"
                  >
                    <Save className="w-5 h-5" />
                  </button>
                  {isSaveDropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-72 md:w-80 bg-white rounded-[2rem] shadow-2xl border border-slate-100 z-50 p-6 animate-scale-up">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-black text-[11px] uppercase tracking-widest text-slate-900">Save Configuration</h4>
                        <button onClick={() => setIsSaveDropdownOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">File Name</label>
                          <input 
                            type="text" 
                            value={saveFileName}
                            onChange={(e) => setSaveFileName(e.target.value)}
                            className="w-full text-sm font-bold text-slate-700 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            placeholder="My Note Document"
                          />
                        </div>
                        <div>
                          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Destination Folder</label>
                          <select 
                            value={isCreatingFolder ? 'new' : saveFolderId}
                            onChange={(e) => {
                              if (e.target.value === 'new') {
                                setIsCreatingFolder(true);
                                setSaveFolderId('');
                              } else {
                                setIsCreatingFolder(false);
                                setSaveFolderId(e.target.value);
                              }
                            }}
                            className="w-full text-sm font-bold text-slate-700 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none"
                          >
                            <option value="">Root Vault Directory</option>
                            {vaultFolders.map(f => (
                              <option key={f._id} value={f._id}>📁 {f.name}</option>
                            ))}
                            <option value="new">✨ + Create New Folder...</option>
                          </select>
                        </div>
                        {isCreatingFolder && (
                          <div>
                            <input 
                              type="text" 
                              value={newSaveFolderName}
                              onChange={(e) => setNewSaveFolderName(e.target.value)}
                              className="w-full text-sm font-bold text-blue-700 p-3 bg-blue-50 border border-blue-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              placeholder="New folder name..."
                            />
                          </div>
                        )}
                        <div className="border-t border-slate-100 pt-4 mt-2">
                          <label className="block text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Export Format</label>
                          <select 
                            value={saveFormat}
                            onChange={(e) => setSaveFormat(e.target.value)}
                            className="w-full text-sm font-bold text-slate-700 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 appearance-none mb-4"
                          >
                            <option value="html">🌐 HTML Web Page</option>
                            <option value="docx">📝 Word Document (DOCX)</option>
                            <option value="ppt">📊 PowerPoint Slide (PPT)</option>
                            <option value="pdf">📄 Vector PDF Document</option>
                          </select>
                          <button onClick={() => handleSaveNote(saveFormat)} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black text-[11px] uppercase tracking-[0.15em] rounded-xl shadow-xl shadow-blue-500/20 transition-all flex items-center justify-center gap-2">
                            <Save className="w-4 h-4" /> Finalize & Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Editor Body */}
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
              {/* Title Input */}
              <input 
                type="text" 
                value={noteTitle} 
                onChange={e => setNoteTitle(e.target.value)} 
                placeholder="ENTER NOTE TITLE..." 
                className="w-full bg-transparent border-none focus:ring-0 text-xl font-black uppercase tracking-wider text-slate-900 mb-4 px-2 placeholder-slate-300"
              />

              {/* Rich Text Toolbar */}
              <div className="bg-white p-3 rounded-2xl border border-slate-100 flex flex-wrap gap-2 items-center mb-4 shadow-sm select-none">
                <button onClick={() => execEditorCommand('bold')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Bold"><Bold className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('italic')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Italic"><Italic className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('underline')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Underline"><Underline className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('strikeThrough')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Strikethrough"><Strikethrough className="w-4 h-4" /></button>
                
                <div className="h-5 w-px bg-slate-200 mx-1" />

                {/* Heading Styles */}
                <button onClick={() => execEditorCommand('formatBlock', '<h1>')} className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all" title="Heading 1">H1</button>
                <button onClick={() => execEditorCommand('formatBlock', '<h2>')} className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all" title="Heading 2">H2</button>
                <button onClick={() => execEditorCommand('formatBlock', '<p>')} className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all" title="Paragraph">P</button>

                <div className="h-5 w-px bg-slate-200 mx-1" />

                {/* Lists */}
                <button onClick={() => execEditorCommand('insertUnorderedList')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Bullet List"><List className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('insertOrderedList')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Numbered List"><ListOrdered className="w-4 h-4" /></button>

                <div className="h-5 w-px bg-slate-200 mx-1" />

                {/* Alignments */}
                <button onClick={() => execEditorCommand('justifyLeft')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Align Left"><AlignLeft className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('justifyCenter')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Align Center"><AlignCenter className="w-4 h-4" /></button>
                <button onClick={() => execEditorCommand('justifyRight')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Align Right"><AlignRight className="w-4 h-4" /></button>

                <div className="h-5 w-px bg-slate-200 mx-1" />

                {/* Text Color Picker */}
                <div className="relative">
                  <button 
                    onClick={(e) => { e.stopPropagation(); setShowColorPicker(!showColorPicker); }}
                    className={`p-2.5 rounded-xl transition-all flex items-center gap-1 ${showColorPicker ? 'bg-slate-100 text-slate-900' : 'hover:bg-slate-50 text-slate-600 hover:text-slate-900'}`} 
                    title="Text Color"
                  >
                    <Palette className="w-4 h-4" />
                  </button>
                  {showColorPicker && (
                    <>
                      <div className="fixed inset-0 z-[11900]" onClick={() => setShowColorPicker(false)} />
                      <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-xl shadow-lg border border-slate-100 flex gap-2 z-[12000] animate-scale-up">
                        {['#000000', '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed'].map(color => (
                          <button 
                            key={color} 
                            onClick={() => { execEditorCommand('foreColor', color); setShowColorPicker(false); }} 
                            className="w-5 h-5 rounded-full border border-slate-200 hover:scale-110 transition-transform" 
                            style={{ backgroundColor: color }} 
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Custom Link */}
                <button onClick={() => {
                  const url = window.prompt('Enter link URL:');
                  if (url) execEditorCommand('createLink', url);
                }} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-slate-900 rounded-xl transition-all" title="Insert Link"><Link2 className="w-4 h-4" /></button>

                <button onClick={() => execEditorCommand('removeFormat')} className="p-2.5 hover:bg-slate-50 text-slate-600 hover:text-red-500 rounded-xl transition-all ml-auto" title="Clear Formatting"><X className="w-4 h-4" /></button>
              </div>

              {/* Editable Canvas */}
              <div 
                ref={editorRef}
                contentEditable 
                className="flex-1 bg-white rounded-3xl border border-slate-100 p-6 md:p-8 overflow-y-auto text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-slate-800 leading-relaxed shadow-sm min-h-[300px] custom-scrollbar note-canvas"
                placeholder="Start typing your notes here..."
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* SAVE NOTE MODAL */}
      {saveModalOpen && (
        <div className="fixed inset-0 z-[13000] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-md p-8 shadow-2xl animate-scale-up border border-slate-100">
            <h3 className="text-lg font-black uppercase tracking-wider mb-2 text-slate-900">Choose Export Format</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">
              Select a format to compile and secure in your vault
            </p>
            
            <div className="space-y-3">
              {[
                { id: 'html', label: 'Web Page (.html)', desc: 'Full interactive HTML document for direct vault editing' },
                { id: 'docx', label: 'Word Document (.docx)', desc: 'Open in Microsoft Word, Google Docs, or Pages' },
                { id: 'ppt', label: 'PowerPoint Slide (.ppt)', desc: 'Separates headings into styled slide sections' },
                { id: 'pdf', label: 'PDF Document (.pdf)', desc: 'High fidelity print-ready vector document' }
              ].map(format => (
                <button 
                  key={format.id}
                  onClick={() => handleSaveNote(format.id)}
                  className="w-full text-left p-4 hover:bg-slate-50 border border-slate-100 rounded-2xl flex flex-col gap-1 transition-all group hover:border-blue-200"
                >
                  <span className="font-black text-[11px] uppercase tracking-widest text-slate-700 group-hover:text-blue-600 transition-colors">
                    {format.label}
                  </span>
                  <span className="text-[9px] text-slate-400 font-bold group-hover:text-slate-500 transition-colors">
                    {format.desc}
                  </span>
                </button>
              ))}
            </div>
            
            <button 
              onClick={() => setSaveModalOpen(false)} 
              className="w-full mt-6 py-4 bg-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-400 hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* INSERT LINK MODAL */}
      {insertLinkModalOpen && (
        <div className="fixed inset-0 z-[13000] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg p-8 shadow-2xl animate-scale-up border border-slate-100 flex flex-col max-h-[80vh]">
            <h3 className="text-lg font-black uppercase tracking-wider mb-2 text-slate-900">Link Personal Asset</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6">
              Select a file from your vault to insert a direct download hyperlink
            </p>
            
            <div className="flex-1 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
              {vaultFiles.length === 0 ? (
                <p className="text-center text-slate-400 py-10 font-bold uppercase text-[10px] tracking-widest">No assets found</p>
              ) : (
                vaultFiles.map(file => (
                  <button 
                    key={file._id}
                    onClick={() => handleInsertFileLink(file)}
                    className="w-full text-left p-4 hover:bg-slate-50 border border-slate-100 rounded-2xl flex items-center gap-4 transition-all group hover:border-blue-200"
                  >
                    <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-all">
                      <FileText className="w-4 h-4" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <span className="block font-black text-[10px] uppercase tracking-widest text-slate-700 truncate">
                        {file.title}
                      </span>
                      <span className="block text-[8px] text-slate-400 font-bold uppercase tracking-widest">
                        {file.file?.format?.toUpperCase()} • {Math.round(file.file?.size / 1024)} KB
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
            
            <button 
              onClick={() => setInsertLinkModalOpen(false)} 
              className="w-full mt-6 py-4 bg-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-400 hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {shareModalOpen && (
        <div className="fixed inset-0 z-[14000] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] w-full max-w-sm p-8 shadow-2xl animate-scale-up border border-slate-100 flex flex-col">
            <h3 className="text-lg font-black uppercase tracking-wider mb-2 text-slate-900 text-center">Share Asset</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-6 text-center">
              Distribute via social networks
            </p>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button 
                onClick={() => {
                  const url = encodeURIComponent(personalStorageAPI.downloadFile(shareFileId));
                  window.open(`https://t.me/share/url?url=${url}&text=Check out this material!`, '_blank');
                  setShareModalOpen(false);
                }}
                className="flex flex-col items-center justify-center p-4 bg-blue-50 text-blue-500 rounded-2xl hover:bg-blue-500 hover:text-white transition-all group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white mb-2 shadow-sm group-hover:bg-blue-400 text-blue-500 group-hover:text-white">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.223-.535.223l.188-2.85 5.18-4.68c.223-.198-.054-.308-.346-.11l-6.4 4.03-2.76-.86c-.6-.188-.612-.604.126-.893l10.81-4.17c.502-.186.945.115.787.898z"/></svg>
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest">Telegram</span>
              </button>
              
              <button 
                onClick={() => {
                  const url = encodeURIComponent(personalStorageAPI.downloadFile(shareFileId));
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                  setShareModalOpen(false);
                }}
                className="flex flex-col items-center justify-center p-4 bg-blue-50 text-blue-700 rounded-2xl hover:bg-blue-600 hover:text-white transition-all group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white mb-2 shadow-sm group-hover:bg-blue-500 text-blue-700 group-hover:text-white">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest">Facebook</span>
              </button>

              <button 
                onClick={() => {
                  const url = encodeURIComponent(personalStorageAPI.downloadFile(shareFileId));
                  window.open(`https://wa.me/?text=Check%20out%20this%20material!%20${url}`, '_blank');
                  setShareModalOpen(false);
                }}
                className="flex flex-col items-center justify-center p-4 bg-green-50 text-green-600 rounded-2xl hover:bg-green-500 hover:text-white transition-all group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white mb-2 shadow-sm group-hover:bg-green-400 text-green-600 group-hover:text-white">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.996 0A12 12 0 000 12a12.02 12.02 0 001.622 6.044L.014 24l6.096-1.597A11.968 11.968 0 0011.996 24C18.625 24 24 18.625 24 12S18.625 0 11.996 0zM12 22a9.97 9.97 0 01-5.111-1.408l-.367-.217-3.799.996.999-3.702-.239-.38A9.976 9.976 0 012.004 12 10.005 10.005 0 1112 22zm5.498-7.502c-.301-.151-1.782-.879-2.059-.979-.277-.101-.479-.151-.68.151-.202.302-.782.979-.958 1.181-.176.202-.353.226-.654.076-.302-.151-1.272-.469-2.423-1.498-.895-.801-1.5-1.79-1.676-2.093-.176-.302-.019-.465.132-.615.136-.135.301-.352.452-.529.151-.176.202-.302.302-.503.1-.202.05-.378-.025-.529-.076-.151-.68-1.637-.932-2.241-.246-.59-.496-.51-.68-.52-.176-.01-.378-.01-.579-.01a1.109 1.109 0 00-.805.378c-.277.302-1.057 1.032-1.057 2.516s1.082 2.919 1.233 3.121c.151.202 2.129 3.251 5.155 4.557.72.311 1.282.497 1.721.636.723.23 1.381.197 1.9.12.58-.086 1.782-.728 2.033-1.432.252-.704.252-1.308.176-1.432-.075-.125-.276-.201-.578-.352z"/></svg>
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest">WhatsApp</span>
              </button>

              <button 
                onClick={() => {
                  const url = personalStorageAPI.downloadFile(shareFileId);
                  navigator.clipboard.writeText(url);
                  setSuccess('Link copied to clipboard!');
                  setShareModalOpen(false);
                }}
                className="flex flex-col items-center justify-center p-4 bg-slate-50 text-slate-600 rounded-2xl hover:bg-slate-200 transition-all group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white mb-2 shadow-sm group-hover:bg-slate-300">
                  <Copy className="w-5 h-5 text-slate-600" />
                </div>
                <span className="text-[9px] font-black uppercase tracking-widest">Copy Link</span>
              </button>
            </div>
            
            <button 
              onClick={() => setShareModalOpen(false)} 
              className="w-full mt-2 py-4 bg-slate-100 rounded-2xl font-black text-[10px] uppercase tracking-widest text-slate-400 hover:bg-slate-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!embeddedVault && <Footer />}
      <style dangerouslySetInnerHTML={{
        __html: `
        .animate-slide-up { animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-slide-right { animation: slideRight 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-scale-up { animation: scaleUp 0.2s cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes slideRight { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes scaleUp { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E2E8F0; border-radius: 10px; }
        
        /* Note Canvas CSS List Override */
        .note-canvas ul {
          list-style-type: disc !important;
          margin-left: 2rem !important;
          padding-left: 0.5rem !important;
          list-style-position: outside !important;
        }
        .note-canvas ol {
          list-style-type: decimal !important;
          margin-left: 2rem !important;
          padding-left: 0.5rem !important;
          list-style-position: outside !important;
        }
        .note-canvas li {
          display: list-item !important;
          margin-bottom: 0.25rem !important;
        }
      `}} />
    </div>
  );
};

export default TeacherPortal;